import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, CheckboxField, Field, SelectField, StatusBadge, type Column } from "@fawkes/ui";
import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/**
 * Ophaalkosten — de standaard ophaalregel en de afwijkende afspraken per praktijk, naar de mockup.
 *
 * <p>De regel is één tarief met een kilometergrens: tot en met de grens geldt het ene starttarief,
 * erboven het andere, plus een prijs per kilometer boven de vrije kilometers en een spoedtoeslag.
 * Wijzigen maakt een nieuwe versie met een ingangsdatum; de vorige blijft staan, want een factuur
 * die toen is opgesteld verwijst naar de regel van toen.
 *
 * <p>De proefberekening rekent zichtbaar voor wat een rit kost — dezelfde som als het dossier maakt,
 * zodat je een regel kunt controleren voordat hij ingaat.
 *
 * <p>De praktijkafspraken wonen op de relatie (tabblad Dierenarts); hier staan ze bij elkaar zodat
 * je ziet welke praktijken van de standaardregel afwijken.
 */

interface Ophaalregel {
  id: string;
  version: number | null;
  naam: string;
  starttarief: number | null;
  starttariefVer: number | null;
  grensKm: number | null;
  prijsPerKm: number | null;
  vrijeKm: number | null;
  spoedtoeslag: number | null;
  btwPercentage: number | null;
  retourRekenen: boolean | null;
  ingangsdatum: string | null;
  actief: boolean | null;
}

interface Praktijkafspraak {
  id: number;
  relatieId: number;
  relatieNaam: string;
  vervoersprijs: number | null;
  vergoeding: number | null;
  ingangsdatum: string | null;
  status: "ACTIEF" | "GEPLAND";
  interneOpmerking: string | null;
}

type Vorm = {
  naam: string;
  starttarief: string;
  starttariefVer: string;
  grensKm: string;
  prijsPerKm: string;
  vrijeKm: string;
  spoedtoeslag: string;
  btwPercentage: string;
  retourRekenen: boolean;
  ingangsdatum: string;
};

const num = (s: string): number | null => {
  const n = Number(String(s).replace(",", "."));
  return s.trim() === "" || Number.isNaN(n) ? null : n;
};

/** Dezelfde som als het dossier: de rit in kilometers naar een bedrag exclusief btw. */
export function berekenRit(regel: Ophaalregel, enkeleReisKm: number, spoed: boolean): { km: number; start: number; extraKm: number; bedrag: number } {
  const km = regel.retourRekenen === false ? enkeleReisKm : enkeleReisKm * 2;
  const grens = regel.grensKm ?? null;
  const start = grens != null && regel.starttariefVer != null && enkeleReisKm > grens
    ? regel.starttariefVer
    : (regel.starttarief ?? 0);
  const extraKm = Math.max(0, km - (regel.vrijeKm ?? 0));
  const bedrag = start + extraKm * (regel.prijsPerKm ?? 0) + (spoed ? (regel.spoedtoeslag ?? 0) : 0);
  return { km, start, extraKm, bedrag: Math.round(bedrag * 100) / 100 };
}

export function CrematoriumVervoerOverview({ onOpenRelatie }: { onOpenRelatie?: (relatieId: number) => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const regels = useCustom<Ophaalregel[]>({ url: "crematorium/vervoerstarieven", method: "get" });
  const alle = Array.isArray(regels.data?.data) ? regels.data!.data : [];
  // De regel die geldt: de actieve met de laatste ingangsdatum die niet in de toekomst ligt.
  const actief = useMemo(() => {
    const vandaag = todayIso();
    return [...alle]
      .filter((r) => r.actief !== false && (!r.ingangsdatum || r.ingangsdatum <= vandaag))
      .sort((a, b) => (b.ingangsdatum ?? "").localeCompare(a.ingangsdatum ?? ""))[0] ?? null;
  }, [alle]);

  const afspraken = useCustom<Praktijkafspraak[]>({
    url: "crematorium/vergoedingen?rol=DIERENARTS&alleenActief=false",
    method: "get",
  });
  const afspraakRijen = (Array.isArray(afspraken.data?.data) ? afspraken.data!.data : [])
    .filter((a) => a.vervoersprijs != null);

  // ── bewerken: een nieuwe versie van de regel ──
  const [bewerken, setBewerken] = useState(false);
  const [vorm, setVorm] = useState<Vorm | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const { mutate, isLoading: opslaan } = useCustomMutation();

  const openBewerken = () => {
    setFout(null);
    setVorm({
      naam: actief?.naam ?? t("crematoriumVervoer.standaardregel"),
      starttarief: actief?.starttarief != null ? String(actief.starttarief) : "",
      starttariefVer: actief?.starttariefVer != null ? String(actief.starttariefVer) : "",
      grensKm: actief?.grensKm != null ? String(actief.grensKm) : "15",
      prijsPerKm: actief?.prijsPerKm != null ? String(actief.prijsPerKm) : "",
      vrijeKm: actief?.vrijeKm != null ? String(actief.vrijeKm) : "0",
      spoedtoeslag: actief?.spoedtoeslag != null ? String(actief.spoedtoeslag) : "",
      btwPercentage: actief?.btwPercentage != null ? String(actief.btwPercentage) : "9",
      retourRekenen: actief?.retourRekenen !== false,
      ingangsdatum: todayIso(),
    });
    setBewerken(true);
  };

  const bewaar = () => {
    if (!vorm) return;
    if (num(vorm.starttarief) == null || num(vorm.prijsPerKm) == null) {
      setFout(t("crematoriumVervoer.fout.verplicht"));
      return;
    }
    setFout(null);
    mutate(
      {
        url: "crematorium/vervoerstarieven",
        method: "post",
        values: {
          naam: vorm.naam.trim() || t("crematoriumVervoer.standaardregel"),
          starttarief: num(vorm.starttarief),
          starttariefVer: num(vorm.starttariefVer),
          grensKm: num(vorm.grensKm),
          prijsPerKm: num(vorm.prijsPerKm),
          vrijeKm: num(vorm.vrijeKm) ?? 0,
          spoedtoeslag: num(vorm.spoedtoeslag),
          btwPercentage: num(vorm.btwPercentage),
          retourRekenen: vorm.retourRekenen,
          ingangsdatum: vorm.ingangsdatum || todayIso(),
          actief: true,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => { setBewerken(false); regels.refetch(); },
        onError: (e) => setFout((e as { message?: string })?.message ?? t("crematoriumVervoer.fout.opslaan")),
      },
    );
  };

  // ── proefberekening ──
  const [proefKm, setProefKm] = useState("21");
  const [proefSpoed, setProefSpoed] = useState(false);
  const proef = actief && num(proefKm) != null ? berekenRit(actief, num(proefKm)!, proefSpoed) : null;

  const veld = (label: string, key: keyof Vorm, extra?: { type?: string }) => (
    <Field
      label={label}
      type={extra?.type}
      value={String(vorm?.[key] ?? "")}
      onChange={(e) => setVorm((v) => (v ? { ...v, [key]: e.target.value } : v))}
    />
  );

  const afspraakKolommen: Column<Praktijkafspraak>[] = [
    {
      header: t("crematoriumVervoer.afspraak.col.praktijk"),
      cell: (a) => (
        <button type="button" className="fawkes-link" onClick={() => onOpenRelatie?.(a.relatieId)}>{a.relatieNaam}</button>
      ),
    },
    { header: t("crematoriumVervoer.afspraak.col.regel"), cell: (a) => a.interneOpmerking || t("crematoriumVervoer.afspraak.vast") },
    { header: t("crematoriumVervoer.afspraak.col.prijs"), cell: (a) => (a.vervoersprijs == null ? "—" : formatEuro(a.vervoersprijs, locale)) },
    { header: t("crematoriumVervoer.afspraak.col.geldigVanaf"), cell: (a) => (a.ingangsdatum ? formatDate(a.ingangsdatum, locale) : "—") },
    {
      header: t("crematoriumVervoer.afspraak.col.status"),
      cell: (a) => (
        <StatusBadge tone={a.status === "ACTIEF" ? "success" : "info"}>
          {a.status === "ACTIEF" ? t("crematoriumVervoer.status.actief") : t("crematoriumVervoer.status.gepland")}
        </StatusBadge>
      ),
    },
  ];

  const geld = (n: number | null | undefined) => (n == null ? "—" : formatEuro(n, locale));

  return (
    <div className="crem-page">
      <div className="crem-page__head">
        <div className="crem-page__intro">
          <div>
            <h2 className="crem-page__title">{t("crematoriumVervoer.title")}</h2>
            <p className="crem-page__sub">{t("crematoriumVervoer.sub")}</p>
          </div>
          <div className="crem-page__actions">
            <Button onClick={openBewerken}>{t("crematoriumVervoer.nieuweVersie")}</Button>
          </div>
        </div>
      </div>

      <div className="crem-cols">
        {/* ── de standaardregel ── */}
        <section className="crem-card" data-testid="ophaalregel">
          <div className="crem-card__head">
            <h3 className="crem-card__title">{t("crematoriumVervoer.standaardregel")}</h3>
            {actief?.ingangsdatum && (
              <span className="fawkes-muted">{t("crematoriumVervoer.actiefSinds")} {formatDate(actief.ingangsdatum, locale)}</span>
            )}
          </div>
          {!bewerken && (
            actief ? (
              <dl className="crem-feiten">
                {/* Zonder kilometergrens is er één starttarief; dan geen "tot en met — km". */}
                {actief.grensKm != null ? (
                  <>
                    <div><dt>{t("crematoriumVervoer.veld.starttariefTot")} {actief.grensKm} km</dt><dd>{geld(actief.starttarief)}</dd></div>
                    <div><dt>{t("crematoriumVervoer.veld.starttariefVanaf")} {actief.grensKm} km</dt><dd>{geld(actief.starttariefVer ?? actief.starttarief)}</dd></div>
                  </>
                ) : (
                  <div><dt>{t("crematoriumVervoer.veld.starttarief")}</dt><dd>{geld(actief.starttarief)}</dd></div>
                )}
                <div><dt>{t("crematoriumVervoer.veld.prijsPerKm")}</dt><dd>{geld(actief.prijsPerKm)}</dd></div>
                <div><dt>{t("crematoriumVervoer.veld.vrijeKm")}</dt><dd>{actief.vrijeKm ?? 0} km</dd></div>
                <div><dt>{t("crematoriumVervoer.veld.spoedtoeslag")}</dt><dd>{geld(actief.spoedtoeslag)}</dd></div>
                <div><dt>{t("crematoriumVervoer.veld.btw")}</dt><dd>{actief.btwPercentage ?? "—"} %</dd></div>
                <div><dt>{t("crematoriumVervoer.veld.retour")}</dt><dd>{actief.retourRekenen === false ? t("common.no") : t("common.yes")}</dd></div>
              </dl>
            ) : (
              <p className="fawkes-muted">{regels.isLoading ? t("common.loading") : t("crematoriumVervoer.empty")}</p>
            )
          )}
          {bewerken && vorm && (
            <div className="asiel-tarief__form">
              {veld(t("crematoriumVervoer.veld.naam"), "naam")}
              {veld(`${t("crematoriumVervoer.veld.starttariefTot")} ${vorm.grensKm || "…"} km`, "starttarief")}
              {veld(`${t("crematoriumVervoer.veld.starttariefVanaf")} ${vorm.grensKm || "…"} km`, "starttariefVer")}
              {veld(t("crematoriumVervoer.veld.grensKm"), "grensKm")}
              {veld(t("crematoriumVervoer.veld.prijsPerKm"), "prijsPerKm")}
              {veld(t("crematoriumVervoer.veld.vrijeKm"), "vrijeKm")}
              {veld(t("crematoriumVervoer.veld.spoedtoeslag"), "spoedtoeslag")}
              <SelectField
                label={t("crematoriumVervoer.veld.btw")}
                value={vorm.btwPercentage}
                onChange={(e) => setVorm((v) => (v ? { ...v, btwPercentage: e.target.value } : v))}
                options={[{ value: "9", label: "9%" }, { value: "21", label: "21%" }]}
              />
              <CheckboxField
                label={t("crematoriumVervoer.veld.retour")}
                checked={vorm.retourRekenen}
                onChange={(e) => setVorm((v) => (v ? { ...v, retourRekenen: e.target.checked } : v))}
              />
              {veld(t("crematoriumVervoer.veld.ingangsdatum"), "ingangsdatum", { type: "date" })}
              {fout && <div className="asiel-tarief__notice asiel-tarief__notice--error">{fout}</div>}
              <div className="asiel-tarief__row-actions">
                <Button variant="secondary" onClick={() => setBewerken(false)}>{t("common.cancel")}</Button>
                <Button onClick={bewaar} disabled={opslaan}>{t("common.save")}</Button>
              </div>
            </div>
          )}
        </section>

        {/* ── proefberekening ── */}
        <section className="crem-card" data-testid="proefberekening">
          <div className="crem-card__head">
            <h3 className="crem-card__title">{t("crematoriumVervoer.proef.title")}</h3>
            <span className="fawkes-muted">{t("crematoriumVervoer.proef.exclBtw")}</span>
          </div>
          <div className="asiel-tarief__toolbar">
            <Field label={t("crematoriumVervoer.proef.enkeleReis")} inputMode="decimal" value={proefKm} onChange={(e) => setProefKm(e.target.value)} />
            <SelectField
              label={t("crematoriumVervoer.proef.moment")}
              value={proefSpoed ? "spoed" : "regulier"}
              onChange={(e) => setProefSpoed(e.target.value === "spoed")}
              options={[
                { value: "regulier", label: t("crematoriumVervoer.proef.regulier") },
                { value: "spoed", label: t("crematoriumVervoer.proef.spoed") },
              ]}
            />
          </div>
          {proef ? (
            <p className="crem-totaal" data-testid="proef-uitkomst">
              {proef.km} km {actief?.retourRekenen === false ? "" : t("crematoriumVervoer.proef.retour")} · {t("crematoriumVervoer.proef.start")} {geld(proef.start)}
              {proef.extraKm > 0 && <> + {proef.extraKm} km × {geld(actief?.prijsPerKm)}</>}
              {proefSpoed && <> + {t("crematoriumVervoer.veld.spoedtoeslag").toLowerCase()}</>}
              {" "}= <strong>{geld(proef.bedrag)}</strong>
            </p>
          ) : (
            <p className="fawkes-muted">{t("crematoriumVervoer.proef.geenRegel")}</p>
          )}
          <p className="fawkes-muted">{t("crematoriumVervoer.proef.uitleg")}</p>
        </section>
      </div>

      {/* ── afwijkende praktijkafspraken ── */}
      <section className="crem-card" data-testid="praktijkafspraken">
        <div className="crem-card__head">
          <h3 className="crem-card__title">{t("crematoriumVervoer.afspraken.title")}</h3>
        </div>
        <p className="fawkes-muted">{t("crematoriumVervoer.afspraken.uitleg")}</p>
        <IndeelbareTabel<Praktijkafspraak> tabelKey="crematorium-vervoer-overview-praktijkafspraak"
          columns={afspraakKolommen}
          rows={afspraakRijen}
          getRowKey={(a) => String(a.id)}
          empty={afspraken.isLoading ? t("common.loading") : t("crematoriumVervoer.afspraken.empty")}
        />
      </section>

      {/* ── eerdere versies ── */}
      {alle.length > 1 && (
        <section className="crem-card">
          <div className="crem-card__head">
            <h3 className="crem-card__title">{t("crematoriumVervoer.versies.title")}</h3>
          </div>
          <IndeelbareTabel<Ophaalregel> tabelKey="crematorium-vervoer-overview-ophaalregel"
            columns={[
              { header: t("crematoriumVervoer.veld.ingangsdatum"), cell: (r) => (r.ingangsdatum ? formatDate(r.ingangsdatum, locale) : "—") },
              { header: t("crematoriumVervoer.veld.naam"), cell: (r) => r.naam },
              { header: t("crematoriumVervoer.veld.starttarief"), cell: (r) => geld(r.starttarief) },
              { header: t("crematoriumVervoer.veld.prijsPerKm"), cell: (r) => geld(r.prijsPerKm) },
              { header: t("crematoriumVervoer.veld.spoedtoeslag"), cell: (r) => geld(r.spoedtoeslag) },
              {
                header: t("crematoriumVervoer.afspraak.col.status"),
                cell: (r) => (
                  <StatusBadge tone={r.id === actief?.id ? "success" : "neutral"}>
                    {r.id === actief?.id ? t("crematoriumVervoer.status.actief") : t("crematoriumVervoer.status.vorig")}
                  </StatusBadge>
                ),
              },
            ]}
            rows={[...alle].sort((a, b) => (b.ingangsdatum ?? "").localeCompare(a.ingangsdatum ?? ""))}
            getRowKey={(r) => r.id}
            empty={t("crematoriumVervoer.empty")}
          />
        </section>
      )}
    </div>
  );
}
